import type { Object3D } from 'three';
import { ImportedModelRaycastCandidatePolicy } from './ImportedModelRaycastCandidatePolicy';

/**
 * 由场景成员事件维护物理候选，按当前资格发布可复用的只读快照。
 *
 * 不自行监听 Three.js 子树、不拥有模型，不在 Hover 时发现整棵 Scene；
 * 只验证已知候选的动态资格与相对顺序，保留原有深度优先 tie-break。
 */
export class ImportedModelRaycastCandidateIndex {
  /** 深度优先排列的潜在物理候选。 */
  private readonly objects: Object3D[] = [];
  /** 用于重复注册保护的对象引用集合。 */
  private readonly members = new Set<Object3D>();
  /** 当前资格策略。 */
  private readonly policy: ImportedModelRaycastCandidatePolicy;
  /** 稳定语义分类缓存；成员移除或权威版本变化时显式清空。 */
  private readonly semanticKindByObject = new Map<Object3D, ImportedModelRaycastCandidateSemanticKind>();
  /** 复用资格筛选缓冲。 */
  private readonly eligible: Object3D[] = [];
  /** 当前对外发布的不可变结果，只有实际变化时才复制。 */
  private snapshot: readonly Object3D[] = Object.freeze([]);
  /** 比较场景顺序时复用的两条父链。 */
  private readonly leftPath: Object3D[] = [];
  /** 比较场景顺序时复用的右父链。 */
  private readonly rightPath: Object3D[] = [];
  /** 解除成员订阅的唯一出口。 */
  private readonly unsubscribe: () => void;
  /** 累计动态资格判断次数。 */
  private eligibilityChecks = 0;
  /** 累计发布快照次数。 */
  private publishedSnapshots = 0;
  /** 累计外部顺序修复次数。 */
  private orderRepairs = 0;
  /** 当前物理顺序是否需要在发布前验证。 */
  private orderValidationPending = false;
  /** 累计顺序验证次数。 */
  private orderValidations = 0;
  /** 累计相邻候选顺序比较次数。 */
  private orderComparisons = 0;
  /** 累计成员源顺序失效通知次数。 */
  private orderInvalidations = 0;
  /** 上次同步的 metadata 修订。 */
  private metadataRevision = 0;
  /** 上次同步的 topology 修订。 */
  private topologyRevision = 0;
  /** 上次同步的实例注册表修订。 */
  private instancingRevision = 0;
  /** 累计稳定语义分类次数。 */
  private semanticClassifications = 0;
  /** 累计稳定语义缓存命中次数。 */
  private semanticCacheHits = 0;
  /** 累计权威语义版本变化次数。 */
  private semanticRevisionChanges = 0;
  /** 生命周期状态。 */
  private disposed = false;

  /**
   * 接入统一场景成员来源，立即回放现有对象。
   * @param source 非拥有型成员订阅端口。
   * @param isInstancedMesh 当前正式 Unit 资格查询。
   * @param revisionSource 可选权威语义版本源；缺失时保留逐次解析兼容路径。
   */
  public constructor(
    source: SceneObjectMembershipSource,
    isInstancedMesh: (object: Object3D) => boolean,
    private readonly revisionSource?: ImportedModelRaycastCandidateRevisionSource,
  ) {
    this.policy = new ImportedModelRaycastCandidatePolicy(isInstancedMesh);
    if (revisionSource) {
      this.metadataRevision = revisionSource.getMetadataRevision();
      this.topologyRevision = revisionSource.getTopologyRevision();
      this.instancingRevision = revisionSource.getInstancingRevision();
    }
    this.unsubscribe = source.subscribe({
      onObjectAdded: this.addObject,
      onObjectRemoved: this.removeObject,
      onObjectOrderChanged: this.invalidateOrder,
    });
  }

  /** @returns 当前深度优先、动态资格有效的不可变候选快照；调用方不能原地修改。 */
  public collect(): readonly Object3D[] {
    if (this.disposed) return this.snapshot;
    this.refreshSemanticRevision();
    // 成员新增后的 children 重排由共享成员源显式失效；稳定 Hover 不再逐对验证同一顺序。
    if (this.orderValidationPending) {
      this.orderValidations += 1;
      for (let index = 1; index < this.objects.length; index++) {
        this.orderComparisons += 1;
        if (this.compareSceneOrder(this.objects[index - 1], this.objects[index]) > 0) {
          this.objects.sort(this.compareSceneOrder);
          this.orderRepairs++;
          break;
        }
      }
      this.orderValidationPending = false;
    }
    this.eligible.length = 0;
    for (const object of this.objects) {
      this.eligibilityChecks++;
      if (!this.revisionSource) {
        if (this.policy.accepts(object)) this.eligible.push(object);
        continue;
      }
      let semanticKind = this.semanticKindByObject.get(object);
      if (semanticKind === undefined) {
        semanticKind = this.policy.resolveSemanticKind(object);
        this.semanticKindByObject.set(object, semanticKind);
        this.semanticClassifications += 1;
      } else {
        this.semanticCacheHits += 1;
      }
      if (this.policy.acceptsWithSemanticKind(object, semanticKind)) this.eligible.push(object);
    }
    let changed = this.eligible.length !== this.snapshot.length;
    for (let index = 0; !changed && index < this.eligible.length; index++) {
      if (this.eligible[index] !== this.snapshot[index]) changed = true;
    }
    if (changed) {
      this.snapshot = Object.freeze(this.eligible.slice());
      this.publishedSnapshots++;
    }
    // 父链只用于单次比较，不能在移除成员后间接保留已卸载文档。
    this.leftPath.length = this.rightPath.length = 0;
    return this.snapshot;
  }

  /** @returns 显式调试统计；统计对象不在 Hover 热路径自动创建。 */
  public getStats(): ImportedModelRaycastCandidateStats {
    return {
      physicalCandidates: this.objects.length,
      eligibilityChecks: this.eligibilityChecks,
      publishedSnapshots: this.publishedSnapshots,
      orderRepairs: this.orderRepairs,
      orderValidations: this.orderValidations,
      orderComparisons: this.orderComparisons,
      orderInvalidations: this.orderInvalidations,
      semanticClassifications: this.semanticClassifications,
      semanticCacheHits: this.semanticCacheHits,
      semanticRevisionChanges: this.semanticRevisionChanges,
    };
  }

  /** 解除订阅与旧候选引用，不销毁场景成员源或模型。 */
  public dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribe();
    this.objects.length = this.eligible.length = this.leftPath.length = this.rightPath.length = 0;
    this.members.clear();
    this.semanticKindByObject.clear();
    this.snapshot = Object.freeze([]);
  }

  /** @param object 刚进入所跟踪场景的物理对象。 */
  private readonly addObject = (object: Object3D): void => {
    if (this.disposed || this.members.has(object) || !ImportedModelRaycastCandidatePolicy.isPotentialCandidate(object)) return;
    // 增量二分插入，不因一个 Hover 代理入场就重新遍历整个模型树。
    let low = 0, high = this.objects.length;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (this.compareSceneOrder(this.objects[middle], object) <= 0) low = middle + 1;
      else high = middle;
    }
    this.objects.splice(low, 0, object);
    this.members.add(object);
    this.semanticKindByObject.delete(object);
    this.leftPath.length = this.rightPath.length = 0;
  };

  /** @param object 已离开场景的物理对象。 */
  private readonly removeObject = (object: Object3D): void => {
    if (!this.members.delete(object)) return;
    this.semanticKindByObject.delete(object);
    const index = this.objects.indexOf(object);
    if (index !== -1) this.objects.splice(index, 1);
    // 即使没有下一次 Hover，也必须及时解除离开文档的强引用。
    this.eligible.length = this.leftPath.length = this.rightPath.length = 0;
    if (this.snapshot.includes(object)) this.snapshot = Object.freeze([]);
  };

  /** 成员源已确认同父级顺序变化，下一次发布前只验证一次。 */
  private readonly invalidateOrder = (): void => {
    if (this.disposed) return;
    this.orderValidationPending = true;
    this.orderInvalidations += 1;
  };

  /** 权威语义版本变化时原子清空分类，下一轮逐候选惰性重建。 */
  private refreshSemanticRevision(): void {
    const source = this.revisionSource;
    if (!source) return;
    const metadataRevision = source.getMetadataRevision();
    const topologyRevision = source.getTopologyRevision();
    const instancingRevision = source.getInstancingRevision();
    if (
      metadataRevision === this.metadataRevision
      && topologyRevision === this.topologyRevision
      && instancingRevision === this.instancingRevision
    ) {
      return;
    }
    this.metadataRevision = metadataRevision;
    this.topologyRevision = topologyRevision;
    this.instancingRevision = instancingRevision;
    this.semanticKindByObject.clear();
    this.semanticRevisionChanges += 1;
  }

  /**
   * 只沿两条父链比较相对位置，不遍历无关子树。
   * @param left 左对象。
   * @param right 右对象。
   * @returns 当前场景深度优先顺序。
   */
  private readonly compareSceneOrder = (left: Object3D, right: Object3D): number => {
    if (left === right) return 0;
    this.leftPath.length = this.rightPath.length = 0;
    let current: Object3D | null = left;
    while (current) { this.leftPath.push(current); current = current.parent; }
    current = right;
    while (current) { this.rightPath.push(current); current = current.parent; }
    let leftIndex = this.leftPath.length - 1, rightIndex = this.rightPath.length - 1;
    let commonParent: Object3D | null = null;
    while (leftIndex >= 0 && rightIndex >= 0 && this.leftPath[leftIndex] === this.rightPath[rightIndex]) {
      commonParent = this.leftPath[leftIndex--];
      rightIndex--;
    }
    if (leftIndex < 0) return -1;
    if (rightIndex < 0) return 1;
    if (!commonParent) return left.id - right.id;
    return commonParent.children.indexOf(this.leftPath[leftIndex]) - commonParent.children.indexOf(this.rightPath[rightIndex]);
  };
}
